import { useCallback } from 'react';

/**
 * Custom hook for validation functionality
 * 
 * @param {Object} params - Hook parameters
 * @param {Array} params.organisms - List of organisms
 * @param {Array} params.customOrganisms - Custom organisms
 * @param {Array} params.currentLocations - Current locations list
 * @param {Array} params.locations - Default locations list
 * @param {Object} params.gridData - Algae grid data
 * @param {Array} params.currentZooplanktonOrganisms - Current zooplankton organisms
 * @param {Object} params.zooplanktonGridData - Zooplankton grid data
 * @returns {Object} Validation functions
 */
export function useValidation({
  organisms,
  customOrganisms,
  currentLocations,
  locations,
  gridData,
  currentZooplanktonOrganisms,
  zooplanktonGridData,
  hasZooplankton = true,
  hasAlgae = true
}) {
  /**
   * Validate that all required fields are filled
   * @returns {Array} Array of missing field descriptions
   */
  const validateAllFieldsFilled = useCallback(() => {
    const missingFields = [];
    
    // Use currentLocations if available, otherwise fall back to static locations
    const locationsList = currentLocations.length > 0 ? currentLocations : locations;
    
    
    // Check algae data — only if template has algae
    if (hasAlgae) {
      [...organisms, ...customOrganisms].forEach(organism => {
        if (organism.isSum || organism.isTotal) return; // Skip sum rows
        
        const organismKey = `${organism.group}_${organism.species}`;
        locationsList.forEach(location => {
          const value = gridData[organismKey]?.[location];
          if (!value || !value.trim()) {
            const locationName = location ? (location.split(' - ')[1] || location) : 'ukjent lokalitet';
            missingFields.push(`${organism.group} - ${organism.species} ved ${locationName}`);
          }
        });
      });
    }
    
    // Check zooplankton data — only if template has zooplankton
    if (hasZooplankton) {
      currentZooplanktonOrganisms.forEach(organism => {
        if (organism.organism === 'Manet') return;
        const organismKey = `${organism.sampleType}_${organism.organism}`;
        locationsList.forEach(location => {
          const value = zooplanktonGridData[organismKey]?.[location];
          if (!value || !value.trim()) {
            const locationName = location ? (location.split(' - ')[1] || location) : 'ukjent lokalitet';
            missingFields.push(`Zooplankton ${organism.organism} ved ${locationName}`);
          }
        });
      });
    }
    
    return missingFields;
  }, [organisms, customOrganisms, currentLocations, locations, gridData, currentZooplanktonOrganisms, zooplanktonGridData, hasZooplankton, hasAlgae]);

  /**
   * Get completion percentage
   * @returns {number} Percentage of filled fields (0-100)
   */
  const getCompletionPercentage = useCallback(() => {
    let totalFields = 0;
    let filledFields = 0;
    
    // Use currentLocations if available, otherwise fall back to static locations
    const locationsList = currentLocations.length > 0 ? currentLocations : locations;
    
    // Count algae fields — only if template has algae
    if (hasAlgae) {
      [...organisms, ...customOrganisms].forEach(organism => {
        if (organism.isSum || organism.isTotal) return;
        
        const organismKey = `${organism.group}_${organism.species}`;
        locationsList.forEach(location => {
          totalFields++;
          const value = gridData[organismKey]?.[location];
          if (value && value.trim()) filledFields++;
        });
      });
    }
    
    // Count zooplankton fields — only if template has zooplankton
    if (hasZooplankton) {
      currentZooplanktonOrganisms.forEach(organism => {
        const organismKey = `${organism.sampleType}_${organism.organism}`;
        if (organism.organism === 'Manet') return;
        locationsList.forEach(location => {
          totalFields++;
          const value = zooplanktonGridData[organismKey]?.[location];
          if (value && value.trim()) filledFields++;
        });
      });
    }
    
    return Math.round((filledFields / totalFields) * 100);
  }, [organisms, customOrganisms, currentLocations, locations, gridData, currentZooplanktonOrganisms, zooplanktonGridData, hasZooplankton, hasAlgae]);

  return {
    validateAllFieldsFilled,
    getCompletionPercentage
  };
}
